// Import react.
import * as React from 'react';
// Import hooks.
import {
	useList,
	UseListResults,
} from '@app/hooks/view';
// Import enumerations.
import {
	AppThemeColor,
	RecordStatus,
} from '@app/enumerations';
// Import material ui modules
import {
	CheckCircleOutlineOutlined,
	Delete,
	Domain,
	Edit,
	ReportProblemOutlined,
	Search,
} from '@mui/icons-material';
// Import props.
import {
	SvgIconProps
} from '@mui/material';
// Import api models.
import {
	BuildingResponseCommercialExistingWithStatus,
} from '@app/models/api';

interface UseListBuildingParameters {
	id: string;
	disabled: boolean;
	buildings: BuildingResponseCommercialExistingWithStatus[];
	onViewBuilding: (buildingGuid: string) => void;
	onDeleteBuilding: (buildingGuid: string) => void;
}

type UseListBuildingResults = UseListResults;

const useListBuilding = (params: UseListBuildingParameters): UseListBuildingResults => {

	// #region private methods

	/**
	 * Fetches the matching building based on the building guid.
	 * @param buildingGuid
	 */
	const getBuildingListItemRecord = (buildingGuid: string): BuildingResponseCommercialExistingWithStatus | undefined => {
		return params.buildings.find((building: BuildingResponseCommercialExistingWithStatus) => building.id === buildingGuid);
	}

	/**
	 * Checks to see if the building is complete. 
	 **/
	const isBuildingComplete = (building: BuildingResponseCommercialExistingWithStatus): boolean => {
		return building.status?.statusCode === RecordStatus.COMPLETE;
	}

	/**
	 *  Fetches the title of the building list item.
	 */
	const getHeaderTitle = (building: BuildingResponseCommercialExistingWithStatus, index: number): string => {
		let title: string = `Building ${index}`;
		if (!String.isNullOrWhiteSpace(building.buildingData?.buildingDescription)) {
			title = `${building.buildingData?.buildingDescription}`;
		}
		return title.toUpperCase();
	}

	/**
	 *  Fetches the sub-title of the building list item.
	 */
	const getSubtitle = (building: BuildingResponseCommercialExistingWithStatus): string | undefined => {
		return building.status?.statusCode ?? undefined;
	}

	/**
	 *  Fetches the sub-title icon.
	 */
	const getSubtitleIcon = (building: BuildingResponseCommercialExistingWithStatus): React.ComponentType<SvgIconProps> | undefined => {
		return isBuildingComplete(building)
			? CheckCircleOutlineOutlined
			: ReportProblemOutlined;
	}

	/**
	 *  Fetches the sub-title icon.
	 */
	const getMobileIcon = (building: BuildingResponseCommercialExistingWithStatus): React.ComponentType<SvgIconProps> | undefined => {
		return Domain;
	}

	/**
	 * Fetches all icons for the building list item. 
	 **/
	const getIcons = (building: BuildingResponseCommercialExistingWithStatus): { icon: React.ComponentType<SvgIconProps>, label: string, onClick: () => void }[] => {
		const buildingGuid: string | null = building.id;
		if (buildingGuid === null) {
			return [];
		}

		let icons: { icon: React.ComponentType<SvgIconProps>, label: string, onClick: () => void }[] = [];

		if (!params.disabled) {
			icons.push(
				{
					icon: Edit,
					label: 'Edit Building',
					onClick: () => params.onViewBuilding(buildingGuid),
				}
			);
			icons.push(
				{
					icon: Delete,
					label: 'Remove Building',
					onClick: () => params.onDeleteBuilding(buildingGuid),
				}
			);
		}
		else {
			icons.push(
				{
					icon: Search,
					label: 'View Building',
					onClick: () => params.onViewBuilding(buildingGuid),
				}
			);
		}

		return icons;
	}

	/**
	 * Fetches the list item type based off the building completion status.
	 * @param building
	 */
	const getListItemType = (building: BuildingResponseCommercialExistingWithStatus): AppThemeColor => {
		return isBuildingComplete(building)
			? AppThemeColor.PRIMARY
			: AppThemeColor.WARNING;
	}

	/**
	 * Fetches the list item type for mobile based off the building completion status.
	 * @param building
	 */
	const getListItemTypeMobile = (building: BuildingResponseCommercialExistingWithStatus): AppThemeColor => {
		return isBuildingComplete(building)
			? AppThemeColor.SUCCESS
			: AppThemeColor.WARNING;
	}

	

	// #region id generators

	const generateIdForBuildingListItemElement = (building: BuildingResponseCommercialExistingWithStatus): string => {
		return `${params.id}-buildingListItem-${building.id}`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Setup the 'list' hook.
	const list: UseListResults = useList<BuildingResponseCommercialExistingWithStatus>({
		getListItemRecord: getBuildingListItemRecord,
		generateListItemElementId: generateIdForBuildingListItemElement,
		getHeaderTitle: getHeaderTitle,
		getSubtitle: getSubtitle,
		getSubtitleIcon: getSubtitleIcon,
		getMobileIcon: getMobileIcon,
		getIcons: getIcons,
		getListItemType: getListItemType,
		getListItemTypeMobile: getListItemTypeMobile,
	});

	// #endregion variables


	return {
		...list
	};
}

// Export parameters.
export {
	UseListBuildingParameters,
}

// Export results.
export {
	UseListBuildingResults,
}

export default useListBuilding;